import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Mail, Phone, Calendar, FileText, Palette, Users, Image as ImageIcon } from "lucide-react";
import { type ArtistApplication } from "@/hooks/useArtistApplications";

interface ViewArtistApplicationProps {
  application: ArtistApplication;
  onClose: () => void;
  onEdit: () => void;
}

const getStatusColor = (status: ArtistApplication['status']) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'reviewed':
      return 'bg-blue-100 text-blue-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: ArtistApplication['status']) => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'reviewed':
      return 'Reviewed';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    default:
      return status;
  }
};

export default function ViewArtistApplication({
  application,
  onClose,
  onEdit,
}: ViewArtistApplicationProps) {
  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{application.full_name}</h2>
          <p className="text-gray-600">Artist Application Details</p>
        </div>
        <div className="flex items-center space-x-3">
          <Badge className={`${getStatusColor(application.status)} text-sm font-medium px-3 py-1`}>
            {getStatusLabel(application.status)}
          </Badge>
          <Button variant="outline" onClick={onEdit}>
            Edit Application
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>Personal Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Full Name</label>
                <p className="text-gray-900 font-medium">{application.full_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <a 
                    href={`mailto:${application.email}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {application.email}
                  </a>
                </div>
              </div>
            </div>
            {application.phone && (
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <a 
                    href={`tel:${application.phone}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {application.phone}
                  </a>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-500">Portfolio Link</label>
              <div className="flex items-center space-x-2">
                <ExternalLink className="h-4 w-4 text-gray-400" />
                <a 
                  href={application.portfolio_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium break-all"
                >
                  {application.portfolio_link}
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Art & Collaboration Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Palette className="h-5 w-5" />
              <span>Art & Collaboration</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Art Type</label>
              <p className="text-gray-900 font-medium">{application.art_type}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Collaboration Model</label>
              <p className="text-gray-900 font-medium">{application.collaboration_model}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Bio</label>
              <p className="text-gray-900">{application.bio}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Why Brutt?</label>
              <p className="text-gray-900">{application.why_brutt}</p>
            </div>
          </CardContent>
        </Card>

        {/* Images */}
        {(application.image_1_url || application.image_2_url || application.image_3_url) && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ImageIcon className="h-5 w-5" />
                <span>Portfolio Images</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {application.image_1_url && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">Image 1</label>
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={application.image_1_url}
                        alt="Portfolio Image 1"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400">Image not available</div>';
                        }}
                      />
                    </div>
                    <a 
                      href={application.image_1_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span>View Full Size</span>
                    </a>
                  </div>
                )}
                {application.image_2_url && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">Image 2</label>
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={application.image_2_url}
                        alt="Portfolio Image 2"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400">Image not available</div>';
                        }}
                      />
                    </div>
                    <a 
                      href={application.image_2_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span>View Full Size</span>
                    </a>
                  </div>
                )}
                {application.image_3_url && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-500">Image 3</label>
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={application.image_3_url}
                        alt="Portfolio Image 3"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400">Image not available</div>';
                        }}
                      />
                    </div>
                    <a 
                      href={application.image_3_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span>View Full Size</span>
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timestamps */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>Application Timeline</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Applied On</label>
                <p className="text-gray-900 font-medium">
                  {new Date(application.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Updated</label>
                <p className="text-gray-900 font-medium">
                  {new Date(application.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
